import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  CustomInput,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import { useIntl } from "react-intl";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { SocialProviderId } from "../../../../cores/socialPost/config";
import { PostType } from "../../../../cores/socialPost/enums/postType";
import { useYoutubeCore } from "../../../../cores/youtube";
import { youtubeDirectPostDraftSelector } from "../../../../cores/youtube/store/selector";
import "./YoutubeDirectPostPanel.scss";

const CATEGORIES = [
  { value: "22", labelKey: "app.youtube.category.people" },
  { value: "23", labelKey: "app.youtube.category.comedy" },
  { value: "24", labelKey: "app.youtube.category.entertainment" },
  { value: "25", labelKey: "app.youtube.category.news" },
  { value: "26", labelKey: "app.youtube.category.howto" },
  { value: "27", labelKey: "app.youtube.category.education" },
  { value: "28", labelKey: "app.youtube.category.science" },
];

function hasYoutubeSelected(providers: { id?: string; name?: string }[]) {
  return providers.some(
    (sp) =>
      String(sp?.id ?? sp?.name ?? "").toLowerCase() ===
      SocialProviderId.YouTube.toLowerCase()
  );
}

interface Props {
  companyId: string;
}

export const YoutubeDirectPostPanel = ({ companyId: _companyId }: Props) => {
  const intl = useIntl();
  const { socialProviders, postType } = useSocialPostCore();
  const draft = useSelector(youtubeDirectPostDraftSelector);
  const { setDirectPostDraft } = useYoutubeCore();

  const hasYoutube = useMemo(
    () => hasYoutubeSelected(socialProviders as { id?: string; name?: string }[]),
    [socialProviders]
  );

  const isShort = postType === PostType.SHORT;

  if (!hasYoutube) return null;

  const updateDraft = (partial: Parameters<typeof setDirectPostDraft>[0]) => {
    setDirectPostDraft(partial);
  };

  return (
    <div className="youtube-direct-post-panel">
      <div className="youtube-direct-post-panel__header">
        <span className="youtube-direct-post-panel__badge">
          {isShort ? "Short" : "Video"}
        </span>
        <span className="youtube-direct-post-panel__title">
          {intl.formatMessage({ id: "app.youtube.postOptions" })}
        </span>
      </div>

      <FormGroup className="mb-2">
        <Label className="small font-weight-bold mb-1">
          {intl.formatMessage({ id: "app.youtube.titleLabel" })}
          <span className="text-danger ml-1">*</span>
        </Label>
        <Input
          type="text"
          maxLength={100}
          value={draft.title}
          placeholder={intl.formatMessage({ id: "app.youtube.titlePlaceholder" })}
          onChange={(e) => updateDraft({ title: e.target.value })}
        />
        <small className="text-muted">{draft.title.length}/100</small>
      </FormGroup>

      {!isShort && (
        <FormGroup className="mb-2">
          <Label className="small font-weight-bold mb-1">
            {intl.formatMessage({ id: "app.youtube.categoryLabel" })}
          </Label>
          <Input
            type="select"
            value={draft.category}
            onChange={(e) => updateDraft({ category: e.target.value })}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {intl.formatMessage({ id: cat.labelKey })}
              </option>
            ))}
          </Input>
        </FormGroup>
      )}

      <FormGroup className="mb-2">
        <Label className="small font-weight-bold mb-1">
          {intl.formatMessage({ id: "app.youtube.tagsLabel" })}
        </Label>
        <Input
          type="text"
          value={draft.tags}
          placeholder={intl.formatMessage({ id: "app.youtube.tagsPlaceholder" })}
          onChange={(e) => updateDraft({ tags: e.target.value })}
        />
      </FormGroup>

      <FormGroup className="mb-2">
        <Label className="small font-weight-bold mb-1">
          {intl.formatMessage({ id: "app.youtube.visibilityLabel" })}
        </Label>
        <Input
          type="select"
          value={draft.visibility}
          onChange={(e) =>
            updateDraft({
              visibility: e.target.value as "public" | "unlisted" | "private",
            })
          }
        >
          <option value="public">
            {intl.formatMessage({ id: "app.youtube.visibility.public" })}
          </option>
          <option value="unlisted">
            {intl.formatMessage({ id: "app.youtube.visibility.unlisted" })}
          </option>
          <option value="private">
            {intl.formatMessage({ id: "app.youtube.visibility.private" })}
          </option>
        </Input>
      </FormGroup>

      <FormGroup className="mb-2">
        <CustomInput
          type="switch"
          id="youtube-made-for-kids"
          label={intl.formatMessage({ id: "app.youtube.madeForKids" })}
          checked={draft.madeForKids}
          onChange={(e) => updateDraft({ madeForKids: e.target.checked })}
        />
      </FormGroup>

      <FormGroup className="mb-0">
        <CustomInput
          type="switch"
          id="youtube-notify-subs"
          label={intl.formatMessage({ id: "app.youtube.notifySubscribers" })}
          checked={draft.notifySubscribers}
          onChange={(e) =>
            updateDraft({ notifySubscribers: e.target.checked })
          }
        />
      </FormGroup>
    </div>
  );
};

export default YoutubeDirectPostPanel;
